// PromotionPay.js
import React, {useContext, useState, useMemo} from 'react';
import {Container, Row, Col, Card, Button, Table, ToggleButton, ToggleButtonGroup, Badge} from "react-bootstrap";
import { Context } from "../../index";
import UserPaySubscriptionFull from "../../components/payment/UserPaySubscriptionFull";
import {REGISTRATION_ROUTE} from "../../utils/consts";
import {useNavigate, useSearchParams} from "react-router-dom"; // ← NEW: useSearchParams
import SEO from "../../components/seo/SEO";
import PromotionPaySubscription from "../../components/payment/PromotionPaySubscription";

const PromotionPay = () => {
    const { user } = useContext(Context);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();                 // ← NEW
    const [selectedPrice, setSelectedPrice] = useState(null);
    const [billingCycle, setBillingCycle] = useState("monthly");

    // ── NEW: читаем купон и % скидки из query
    const couponId = searchParams.get('coupon') || null;
    const discountPct = Number(searchParams.get('discount'));
    const hasPct = Number.isFinite(discountPct) && discountPct > 0;

    // Вспомогалки для цен (строки вроде "$19.99 Mo")
    const parsePrice = (str) => {
        if (!str) return null;
        const m = String(str).match(/[\d.,]+/);
        if (!m) return null;
        return Number(String(m[0]).replace(',', ''));
    };
    const fmtMoney = (n) => `$${n.toFixed(2)}`;

    // Список тарифов (как было)
    const plans = [
        {
            name: "ESSENTIAL",
            monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ESSENTIAL_MONTHLY,
            yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ESSENTIAL_EARLY, // NOTE: проверь опечатку YEARLY/EARLY
            Book_Limit: "Up to 5 Books",
            Assign_Limit: "Three at a Time",
            Track_Review: "Full Access",
            Library: "Full Access",
            Library_Filter: "Full Access",
            Photo_Review: "Full Access",
            Search_Buy: "Full Access",
            ReviewerCS: "+100 Virts Extra",
            Trust_Score: "+300 when Buy",
            Support: "Full Access",
            monthlyPrice: "$9.99 Mo",
            yearlyPrice: "$99.99 Yr",
            logo: "https://virtalibry.com/logo/virtalibry_dark_es_no_bg.png"
        },
        {
            name: "PREMIUM",
            monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_MONTHLY,
            yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_EARLY,
            Book_Limit: "Up to 50 Books",
            Assign_Limit: "Five at a Time",
            Track_Review: "Full Access",
            Library: "Full Access",
            Library_Filter: "Full Access",
            Photo_Review: "Full Access",
            Search_Buy: "Full Access",
            ReviewerCS: "Free Usage",
            Trust_Score: "+500 when Buy",
            Support: "Full Access",
            monthlyPrice: "$19.99 Mo",
            yearlyPrice: "$199.99 Yr",
            logo: "https://virtalibry.com/logo/virtalibry_dark_pr_no_bg.png"
        },
        {
            name: "MAXIMUM",
            monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_MAXIMUM_MONTHLY,
            yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_MAXIMUM_EARLY,
            Book_Limit: "Up to 500 Books",
            Assign_Limit: "Ten at a Time",
            Track_Review: "Full Access",
            Library: "Full Access",
            Library_Filter: "Full Access",
            Photo_Review: "Full Access",
            Search_Buy: "Full Access",
            ReviewerCS: "Free Usage",
            Trust_Score: "+1000 when Buy",
            Support: "Full Access",
            monthlyPrice: "$29.99 Mo",
            yearlyPrice: "$299.99 Yr",
            logo: "https://virtalibry.com/logo/virtalibry_dark_max_no_bg.png"
        }
    ];

    // ── NEW: предрасчёт цен с учётом скидки
    const pricedPlans = useMemo(() => {
        return plans.map(p => {
            const baseMonthly = parsePrice(p.monthlyPrice);
            const baseYearly  = parsePrice(p.yearlyPrice);

            const monthly = (baseMonthly != null && hasPct)
                ? Math.max(0, baseMonthly * (1 - discountPct/100))
                : baseMonthly;

            const yearly = (baseYearly != null && hasPct)
                ? Math.max(0, baseYearly * (1 - discountPct/100))
                : baseYearly;

            return {
                ...p,
                _baseMonthly: baseMonthly,
                _baseYearly : baseYearly,
                _discMonthly: monthly,
                _discYearly : yearly,
            };
        });
    }, [plans, hasPct, discountPct]);

    // Когда выбран тариф — передаём ещё и купон в компонент оплаты:
    // <UserPaySubscriptionFull priceId=… couponId=… discountPct=… />
    return (
        <>
            <SEO
                url="https://virtalibry.com"
                canonical="https://virtalibry.com"
                organization={{
                    name: "Virtalibry",
                    url: "https://virtalibry.com",
                    logo: "https://virtalibry.com/face/why_love_is_gold_new.png",
                    sameAs: ["https://twitter.com/virtalibry"],
                    contactPoint: [{ "@type": "ContactPoint", contactType: "customer support", email: "info@virtalibry.com" }],
                }}
            />

            {/* Шапка скидки (если есть) */}
            {hasPct && (
                <Row className="justify-content-center">
                    <Col xs="auto" className="mb-3">
                        <Badge bg="success" className="px-3 fs-6 p-2 py-2">
                            Promotion Discount Applied: −{discountPct}%
                        </Badge>
                    </Col>
                </Row>
            )}

            <Row className="justify-content-center">
                {selectedPrice ? null : (
                    <Col xs={12} className="text-center mb-4">
                        <ToggleButtonGroup type="radio" className="btn-group-sm" name="billingCycle" value={billingCycle} onChange={setBillingCycle}>
                            <ToggleButton id="monthly" variant={billingCycle === "monthly" ? "success" : "outline-success"} value="monthly">
                                Monthly
                            </ToggleButton>
                            <ToggleButton id="yearly" variant={billingCycle === "yearly" ? "success" : "outline-success"} value="yearly">
                                Yearly (Save 20%)
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                )}

                {selectedPrice ? (
                    <PromotionPaySubscription
                        priceId={selectedPrice}
                        couponId={couponId || undefined}           // ← NEW: пробрасываем купон
                        discountPct={hasPct ? discountPct : undefined} // ← опционально, для UI внутри
                    />
                ) : (
                    <Container style={{ maxWidth: "1200px" }} className="w-100">
                        <Row className="justify-content-center">
                            {pricedPlans.map((plan) => {
                                // какие цены показывать
                                const showMonthly = billingCycle === "monthly";
                                const base = showMonthly ? plan._baseMonthly : plan._baseYearly;
                                const disc = showMonthly ? plan._discMonthly : plan._discYearly;

                                const priceText = () => {
                                    if (base == null) return showMonthly ? plan.monthlyPrice : plan.yearlyPrice; // FREE
                                    if (hasPct && disc != null) {
                                        return (
                                            <>
                        <span className="text-muted text-decoration-line-through me-2">
                          {fmtMoney(base)}
                        </span>
                                                <strong className="text-success">{fmtMoney(disc)}</strong>
                                            </>
                                        );
                                    }
                                    return <span className="text-muted">{fmtMoney(base)}</span>;
                                };

                                const disabled = !user?.user?.id
                                    ? false
                                    : user.user.tariff_plan === plan.name || (plan.name === "FREE" && user.user.tariff_plan !== "FREE");

                                const nextPriceId = showMonthly ? plan.monthlyPriceId : plan.yearlyPriceId;

                                return (
                                    <Col key={plan.name} xs={12} sm={6} md={4} lg={3} xl={3} className="mb-4 d-flex justify-content-center">
                                        <Card
                                            className={`shadow-sm ${user.user.tariff_plan === plan.name ? "shadow-lg" : ""}`}
                                            style={{
                                                borderRadius: "6px",
                                                transition: "transform 0.2s, box-shadow 0.2s",
                                                cursor: "pointer",
                                                width: "100%",
                                                maxWidth: "300px",
                                                boxShadow: user.user.tariff_plan === plan.name
                                                    ? "0px 6px 16px rgba(0, 0, 0, 0.2)"
                                                    : "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                                transform: user.user.tariff_plan === plan.name ? "translateY(-5px)" : "translateY(0)",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = "0px 6px 16px rgba(0, 0, 0, 0.2)";
                                                e.currentTarget.style.transform = "translateY(-5px)";
                                            }}
                                            onMouseLeave={(e) => {
                                                if (user.user.tariff_plan !== plan.name) {
                                                    e.currentTarget.style.boxShadow = "0px 4px 12px rgba(0, 0, 0, 0.1)";
                                                    e.currentTarget.style.transform = "translateY(0)";
                                                }
                                            }}
                                        >
                                            <Card.Body className="d-flex flex-column align-items-center text-center">
                                                <img
                                                    src={plan.logo}
                                                    alt="Virtalibry Logo"
                                                    className="img-fluid mx-auto mb-3"
                                                    style={{ maxWidth: "150px", height: "auto" }}
                                                />
                                                <h6 className="fw-bold text-success">
                                                    {plan.name} <span className="fst-italic ms-2">{priceText()}</span>
                                                </h6>

                                                <Table striped bordered size="sm" className="table-fixed text-muted small">
                                                    <tbody>
                                                    {[
                                                        ["Book Limit", plan.Book_Limit],
                                                        ["Assign Limit", plan.Assign_Limit],
                                                        ["Track Review", plan.Track_Review],
                                                        ["Library", plan.Library],
                                                        ["Library Filter", plan.Library_Filter],
                                                        ["Photo Review", plan.Photo_Review],
                                                        ["Search&Buy", plan.Search_Buy],
                                                        ["Reviewer CS", plan.ReviewerCS],
                                                        ["Trust Score", plan.Trust_Score],
                                                        ["Support", plan.Support]
                                                    ].map(([key, value], idx) => (
                                                        <tr key={idx}>
                                                            <td className="text-start">{key}:</td>
                                                            <td className="text-start">{value}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>

                                                <Button
                                                    variant="success"
                                                    className="mt-2"
                                                    disabled={disabled}
                                                    onClick={() => {
                                                        if (!user?.user?.id) {
                                                            navigate(REGISTRATION_ROUTE);
                                                        } else {
                                                            // Сохраняем выбранный priceId — внизу отрисуется UserPaySubscriptionFull
                                                            setSelectedPrice(nextPriceId);
                                                        }
                                                    }}
                                                >
                                                    {!user?.user?.id
                                                        ? 'Join Virtalibry Now'
                                                        : user.user.tariff_plan === plan.name
                                                            ? "Your current plan"
                                                            : plan.name === "FREE" && user.user.tariff_plan !== "FREE"
                                                                ? "Unavailable"
                                                                : "Subscribe"}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Container>
                )}
            </Row>
        </>
    );
};

export default PromotionPay;
